/**
 * Color utility functions for White Label feature
 * Handles color contrast calculations and color manipulation
 */

// Convert hex color to RGB
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : null
}

// Convert RGB to hex
export const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (c: number) => {
    const hex = Math.max(0, Math.min(255, Math.round(c))).toString(16)
    return hex.length === 1 ? `0${hex}` : hex
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// Calculate relative luminance according to WCAG 2.0
export const getLuminance = (r: number, g: number, b: number): number => {
  const [rs, gs, bs] = [r, g, b].map(c => {
    const sRGB = c / 255
    return sRGB <= 0.03928 ? sRGB / 12.92 : ((sRGB + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
}

// Calculate contrast ratio between two colors
export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1)
  const rgb2 = hexToRgb(color2)

  if (!rgb1 || !rgb2) return 1

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b)
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b)

  const lighter = Math.max(lum1, lum2)
  const darker = Math.min(lum1, lum2)

  return (lighter + 0.05) / (darker + 0.05)
}

// Determine if white or black text has better contrast on given background
export const getContrastingTextColor = (backgroundColor: string): string => {
  const rgb = hexToRgb(backgroundColor)
  if (!rgb) return '#000000'

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b)

  // Use white text for dark backgrounds, black for light backgrounds
  // WCAG recommends a contrast ratio of at least 4.5:1 for normal text
  return luminance > 0.179 ? '#000000' : '#FFFFFF'
}

// Calculate secondary color (button text color) based on primary color
export const calculateSecondaryColor = (primaryColor: string): string => getContrastingTextColor(primaryColor)

// Validate hex color format
export const isValidHexColor = (color: string): boolean => /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)

// Lighten a color by a percentage
export const lightenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex

  const amount = Math.round(255 * (percent / 100))

  return rgbToHex(
    Math.min(255, rgb.r + amount),
    Math.min(255, rgb.g + amount),
    Math.min(255, rgb.b + amount),
  )
}

// Darken a color by a percentage
export const darkenColor = (hex: string, percent: number): string => {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex

  const amount = Math.round(255 * (percent / 100))

  return rgbToHex(
    Math.max(0, rgb.r - amount),
    Math.max(0, rgb.g - amount),
    Math.max(0, rgb.b - amount),
  )
}
